import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Download, Upload } from 'lucide-react';
import { apiErrorMessage } from '../lib/api';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Alert } from './ui/Alert';
import type { CsvImportResult } from '../types/api';

function csvEscape(value: string) {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function downloadTemplate(filename: string, headers: string[], example: string[]) {
  const csv = [headers, example].map((row) => row.map(csvEscape).join(',')).join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  templateFilename: string;
  templateHeaders: string[];
  templateExampleRow: string[];
  importFn: (file: File) => Promise<CsvImportResult>;
  onImported?: () => void;
}

// Shared by the master catalog and customer catalog pages - upload a CSV,
// see created/updated/skipped counts with per-row reasons for anything
// skipped, and a "download template" link so the columns don't have to be
// guessed or copied from docs.
export function CsvImportModal({
  open,
  onClose,
  title,
  description,
  templateFilename,
  templateHeaders,
  templateExampleRow,
  importFn,
  onImported,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [result, setResult] = useState<CsvImportResult | null>(null);

  const mutation = useMutation({
    mutationFn: (file: File) => importFn(file),
    onSuccess: (res) => {
      setResult(res);
      onImported?.();
    },
  });

  function handleClose() {
    setResult(null);
    mutation.reset();
    onClose();
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setResult(null);
    mutation.mutate(file);
    e.target.value = '';
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      footer={
        <Button variant="secondary" type="button" onClick={handleClose}>
          {result ? 'Done' : 'Cancel'}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-slate-500">{description}</p>

        <button
          type="button"
          onClick={() => downloadTemplate(templateFilename, templateHeaders, templateExampleRow)}
          className="inline-flex w-fit items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-700"
        >
          <Download className="h-3.5 w-3.5" /> Download CSV template
        </button>

        {mutation.isError && <Alert tone="error">{apiErrorMessage(mutation.error, 'Could not import the file.')}</Alert>}

        {result ? (
          <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm">
            <div className="flex gap-4">
              <span className="text-slate-700">
                <span className="font-semibold text-slate-900">{result.created}</span> created
              </span>
              <span className="text-slate-700">
                <span className="font-semibold text-slate-900">{result.updated}</span> updated
              </span>
              <span className="text-slate-700">
                <span className="font-semibold text-slate-900">{result.skipped.length}</span> skipped
              </span>
            </div>
            {result.skipped.length > 0 && (
              <ul className="flex flex-col gap-1 border-t border-slate-200 pt-3 text-xs text-slate-500">
                {result.skipped.map((s, i) => (
                  <li key={i}>
                    Row {s.row}: {s.reason}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <div>
            <input ref={fileInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleFileChange} />
            <Button type="button" variant="secondary" size="sm" isLoading={mutation.isPending} onClick={() => fileInputRef.current?.click()}>
              <Upload className="h-3.5 w-3.5" /> Choose CSV file
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
